import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Pencil, ExternalLink, X, Save, Loader2, Upload, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useCMS } from '../../context/CMSContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { ProductImage } from '../../components/VialIllustration';

const EMPTY_FORM = {
  name: '', category: 'Scientific Products', price: 0, purity: '99% HPLC',
  image: '', description: '', in_stock: true, featured: false, sort_order: 99,
};
const MAX_UPLOAD = 25 * 1024 * 1024;

// --- Small leaf components ---

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const ProductImagePicker = ({ name, image, uploading, onUpload, onChangeUrl }) => {
  const product = React.useMemo(() => ({ name: name || 'New product', image }), [name, image]);
  return (
    <div className="flex items-start gap-4">
      <div className="w-24 h-24 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
        <ProductImage product={product} className="w-full h-full object-contain p-1" containerClassName="w-full h-full p-1" />
      </div>
      <div className="flex-1 space-y-2">
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-sm font-medium cursor-pointer">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading...' : 'Upload vial photo'}
          <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
        </label>
        <input value={image} onChange={(e) => onChangeUrl(e.target.value)} placeholder="or paste image URL" className="input text-xs" />
        {!image && <p className="text-[11px] text-slate-500">No image uploaded — the FAT BUSTERS branded vial will be used as a placeholder.</p>}
        {image && (
          <button type="button" onClick={() => onChangeUrl('')} className="text-[11px] text-slate-500 hover:text-rose-600 underline">
            Remove image (use branded placeholder)
          </button>
        )}
      </div>
    </div>
  );
};

// --- Helpers extracted so ProductForm stays small ---

const uploadProductPhoto = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/uploads', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return data.url;
};

const friendlyUploadError = (err) => err?.response?.data?.detail
  || (err?.code === 'ECONNABORTED' ? 'Upload timed out — try a smaller image or check your connection' : null)
  || err?.message
  || 'Upload failed';

const ProductFormFields = ({ form, set }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Name"><input required value={form.name} onChange={(e) => set('name', e.target.value)} className="input" /></Field>
      <Field label="Category">
        <select value={form.category} onChange={(e) => set('category', e.target.value)} className="input">
          <option>Scientific Products</option>
          <option>Bacteriostatic Water</option>
          <option>Bundles</option>
        </select>
      </Field>
      <Field label="Price (AUD)"><input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => set('price', parseFloat(e.target.value) || 0)} className="input" /></Field>
      <Field label="Purity label"><input value={form.purity} onChange={(e) => set('purity', e.target.value)} className="input" /></Field>
      <Field label="Sort order"><input type="number" value={form.sort_order} onChange={(e) => set('sort_order', parseInt(e.target.value) || 0)} className="input" /></Field>
      <Field label="Slug (URL)"><input value={form.slug || ''} onChange={(e) => set('slug', e.target.value)} placeholder="auto-from-name" className="input" /></Field>
    </div>
    <Field label="Description">
      <textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} className="input resize-none" />
    </Field>
  </>
);

const ProductFormToggles = ({ form, set }) => (
  <div className="flex flex-wrap gap-6 pt-2">
    <label className="inline-flex items-center gap-2 text-sm">
      <input type="checkbox" checked={form.in_stock} onChange={(e) => set('in_stock', e.target.checked)} className="w-4 h-4 accent-red-500" />
      In stock
    </label>
    <label className="inline-flex items-center gap-2 text-sm">
      <input type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)} className="w-4 h-4 accent-red-500" />
      Featured on homepage
    </label>
  </div>
);

const ProductForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD) {
      toast.error('Image too large (max 25MB). Try a smaller photo or screenshot.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const url = await uploadProductPhoto(file);
      set('image', url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(friendlyUploadError(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(form); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <ProductFormFields form={form} set={set} />
      <Field label="Product image">
        <ProductImagePicker
          name={form.name}
          image={form.image}
          uploading={uploading}
          onUpload={upload}
          onChangeUrl={(v) => set('image', v)}
        />
      </Field>
      <ProductFormToggles form={form} set={set} />
      <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">Cancel</button>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />}
          <Save size={14} /> Save
        </button>
      </div>
    </form>
  );
};

// --- Listing row ---

const ProductRow = ({ product, isAdmin, onEdit, onDelete }) => (
  <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_120px_100px_100px_120px] gap-2 md:gap-4 px-4 py-3 items-center border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
    <div className="w-14 h-14 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
      <ProductImage product={product} className="max-w-full max-h-full object-contain p-1" containerClassName="w-full h-full p-1" />
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-[#0a0a0a] text-sm truncate">{product.name}</span>
        {product.featured && <Star size={13} className="text-amber-500 fill-amber-500 flex-shrink-0" />}
      </div>
      <div className="text-xs text-slate-500 truncate">{product.slug}</div>
    </div>
    <div className="text-xs text-slate-600">{product.category}</div>
    <div className="text-sm font-semibold text-red-700">${Number(product.price).toFixed(2)}</div>
    <div>
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${product.in_stock ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
        {product.in_stock ? 'In stock' : 'Restocking'}
      </span>
    </div>
    <div className="flex items-center justify-end gap-1">
      <Link to={`/product/${product.slug}`} target="_blank" className="p-2 text-slate-500 hover:text-red-600 rounded-lg hover:bg-slate-100" title="View"><ExternalLink size={15} /></Link>
      <button onClick={onEdit} className="p-2 text-slate-500 hover:text-red-600 rounded-lg hover:bg-slate-100" title="Edit"><Pencil size={15} /></button>
      {isAdmin && (
        <button onClick={onDelete} className="p-2 text-slate-500 hover:text-red-600 rounded-lg hover:bg-slate-100" title="Delete"><Trash2 size={15} /></button>
      )}
    </div>
  </div>
);

// --- Main tab ---

const ProductsTab = () => {
  const { products, createProduct, updateProduct, deleteProduct } = useCMS();
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(null);

  const handleSave = async (data) => {
    try {
      if (editing === 'new' || !editing?.id) {
        await createProduct(data);
        toast.success('Product created');
      } else {
        await updateProduct(editing.id, data);
        toast.success('Product updated');
      }
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await deleteProduct(p.id);
      toast.success('Product deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  if (editing) {
    return (
      <div className="max-w-3xl bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#0a0a0a]">
            {editing === 'new' ? 'New Product' : `Edit: ${editing.name}`}
          </h2>
          <button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-slate-100"><X size={18} /></button>
        </div>
        <ProductForm
          initial={editing === 'new' ? null : editing}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-[#0a0a0a]">Products ({products.length})</h2>
          <p className="text-sm text-slate-500">Add, edit, or remove products in your catalog.</p>
        </div>
        <button onClick={() => setEditing('new')} className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg">
          <Plus size={16} /> New product
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[80px_1fr_120px_100px_100px_120px] gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
          <span>Image</span><span>Name</span><span>Category</span><span>Price</span><span>Status</span><span className="text-right">Actions</span>
        </div>
        {products.map((p) => (
          <ProductRow key={p.id} product={p} isAdmin={isAdmin} onEdit={() => setEditing(p)} onDelete={() => handleDelete(p)} />
        ))}
      </div>
    </div>
  );
};

export default ProductsTab;
